import {
  type Dispatch,
  PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import { CONSENT_CHANGED, getConsentData } from "@almamedia/consent/v3";
import type { AlmaCustomPurpose, ConsentData } from "@almamedia/consent/v3";

/** Max time to wait for CMP (ms). After this, fetch aborts; purposes stay empty → no consent. */
const CONSENT_TIMEOUT_MS = 5000;

export const REQUIRED_PURPOSES: AlmaCustomPurpose[] = [
  "functional",
  "personalization",
  "analytics"
];

export interface ConsentState {
  purposes: AlmaCustomPurpose[];
  isLoading: boolean;
}

const DEFAULT_STATE: ConsentState = {
  purposes: [],
  isLoading: true
};

export const ConsentContext = createContext<ConsentState>(DEFAULT_STATE);

const updatePurposes = (
  data: ConsentData,
  setPurposes: Dispatch<SetStateAction<AlmaCustomPurpose[]>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>
) => {
  setPurposes(data.purposes);
  setIsLoading(false);
};

export const ConsentProvider = ({ children }: PropsWithChildren) => {
  const [purposes, setPurposes] = useState<AlmaCustomPurpose[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleConsentChanged = useCallback(
    (event: CustomEvent<ConsentData>) => {
      updatePurposes(event.detail, setPurposes, setIsLoading);
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();

    const timeout = globalThis.setTimeout(
      () => controller.abort(),
      CONSENT_TIMEOUT_MS
    );

    getConsentData({ abortSignal: controller.signal })
      .then((data) => {
        globalThis.clearTimeout(timeout);
        updatePurposes(data, setPurposes, setIsLoading);
      })
      .catch((err: unknown) => {
        globalThis.clearTimeout(timeout);
        setIsLoading(false);
        console.error(
          "Consent fetch failed:",
          err instanceof Error ? err.message : String(err)
        );
      });

    document.addEventListener(
      CONSENT_CHANGED,
      handleConsentChanged as EventListener
    );

    return () => {
      controller.abort();
      globalThis.clearTimeout(timeout);
      document.removeEventListener(
        CONSENT_CHANGED,
        handleConsentChanged as EventListener
      );
    };
  }, [handleConsentChanged]);

  const value = useMemo<ConsentState>(
    () => ({ purposes, isLoading }),
    [purposes, isLoading]
  );

  return <ConsentContext value={value}>{children}</ConsentContext>;
};

export default ConsentProvider;
